import { useState, Fragment } from 'react';
import { useReduxState } from 'hooks';
import { getMessageWithPlaceholders } from 'shared-locales';
import { Button } from 'components/common';
import { Dropdown, DropdownButton } from 'components/common/dropdown';
import { Icon } from 'components/common/icons';
import { Form, FormGroup, NumberStepper } from 'components/common/form';
import {
  Modal,
  ModalContent,
  ModalFooter,
  ModalPortal
} from 'components/common/modal';

interface Props {
  blockId: number;
  blockName: string;
  disabled?: boolean;
  handleTaskClick?: (value: boolean) => void;
  isEmbed?: boolean;
  isMandatory?: boolean;
  isTask?: boolean;
  pointsWeight: number;
  onSelection: (value: number) => void;
}

const TaskPointWeightSelector = ({
  blockId,
  blockName,
  disabled = false,
  handleTaskClick,
  isEmbed = false,
  isMandatory = false,
  isTask = true,
  pointsWeight,
  onSelection
}: Props) => {
  const { messages, theme } = useReduxState();

  const [showModal, setShowModal] = useState(false);

  const handleClick = (value: number) => {
    if (value < 6) onSelection(value);
    else setShowModal(true);
  };

  const handleSubmit = ({ taskWeight }: { taskWeight: number }) => {
    onSelection(taskWeight);
    setShowModal(false);
  };

  const getPoints = (value: number) =>
    getMessageWithPlaceholders({
      message: messages.commonNumberOfPoints,
      placeholders: {
        numberOfPoints: value
      }
    });

  const headerTaskText =
    pointsWeight === 0 ? messages.commonNoPoints : getPoints(pointsWeight);

  const options = [
    { value: 0, label: messages.commonNoPoints },
    { value: 1, label: getPoints(1) },
    { value: 2, label: getPoints(2) },
    { value: 3, label: getPoints(3) },
    { value: 4, label: getPoints(4) },
    { value: 5, label: getPoints(5) },
    { value: 6, label: messages.tasksSetPointsWeightManually }
  ];

  return (
    <div className="selector">
      <div className="block-label">
        {blockName}

        {isMandatory ? (
          <span className="block-label-mandatory">
            <span className="block-label-divider">{'•'}</span>
            <span>{messages.commonMandatory}</span>
          </span>
        ) : null}
      </div>

      <div className="points-dropdown">
        <Dropdown
          align="left"
          arrowStyle={{ marginLeft: 3, position: 'relative', top: 1 }}
          buttonStyle={{
            backgroundColor: 'transparent',
            color: theme.textColorLighter,
            fontSize: '1.5rem',
            fontWeight: 600,
            padding: 0,
            position: 'relative',
            left: 5
          }}
          disabled={disabled}
          showArrow={true}
          styleContentButtons={false}
          title={`(${isTask ? headerTaskText : messages.tasksNotATask})`}
          titleStyle={{ marginRight: 2 }}
          tooltip={disabled ? null : messages.tasksSetPoints}
        >
          {isTask ? (
            <>
              {options.map((option, i) => {
                const lastElement = i === options.length - 1;

                return (
                  <Fragment key={i}>
                    <DropdownButton
                      disabled={disabled}
                      isSelected={
                        pointsWeight === option.value && pointsWeight !== 6
                      }
                      key={`point-weight-${blockId}-${option.value}`}
                      onClick={() => !disabled && handleClick(option.value)}
                      text={option.label}
                    />

                    {lastElement && isEmbed ? (
                      <>
                        <div className="dropdown-divider" />
                        <DropdownButton
                          key="isTask"
                          disabled={disabled}
                          onClick={() => !disabled && handleTaskClick(false)}
                          text={
                            <span className="dropdown-info">
                              {messages.tasksSetAsNotTask}
                              <span
                                className="dropdown-info-icon"
                                aria-label={messages.tasksIsTaskTooltip}
                                data-tooltip={messages.tasksIsTaskTooltip}
                              >
                                <Icon icon="question-circle" />
                              </span>
                            </span>
                          }
                        />
                      </>
                    ) : null}
                  </Fragment>
                );
              })}
            </>
          ) : (
            <DropdownButton
              key="isTask"
              disabled={disabled}
              onClick={() => !disabled && handleTaskClick(true)}
              text={
                <span className="dropdown-info">
                  {messages.tasksSetAsTask}
                  <span
                    className="dropdown-info-icon"
                    aria-label={messages.tasksIsTaskTooltip}
                    data-tooltip={messages.tasksIsTaskTooltip}
                  >
                    <Icon icon="question-circle" />
                  </span>
                </span>
              }
            />
          )}
        </Dropdown>
      </div>

      {showModal && !disabled ? (
        <ModalPortal>
          <Form onSubmit={handleSubmit} initialValues={{ taskWeight: 10 }}>
            <Modal
              onHide={() => setShowModal(false)}
              stopPropagation={true}
              title={`${messages.tasksTaskPoints}`}
              visible={showModal}
              width="narrow"
            >
              <ModalContent>
                <FormGroup htmlFor="taskWeight">
                  <NumberStepper
                    field="taskWeight"
                    max={100}
                    min={0}
                    stepValue={1}
                  />
                </FormGroup>
              </ModalContent>
              <ModalFooter>
                <Button
                  category="light"
                  onClick={() => setShowModal(false)}
                  text={messages.commonCancel}
                />
                <Button
                  category="success"
                  type="submit"
                  text={messages.commonConfirm}
                />
              </ModalFooter>
            </Modal>
          </Form>
        </ModalPortal>
      ) : null}

      <style jsx>{`
        .selector {
          display: flex;
          padding-bottom: 10px;
        }
        .block-label {
          color: ${theme.textColorLight};
          display: block;
          font-size: 1.5rem;
          font-weight: 600;
        }
        .block-label-mandatory {
          color: ${theme.textColorLighter};
          font-weight: 400;
        }
        .block-label-divider {
          margin: 0 5px;
        }
        .points-dropdown {
          display: inline-block;
          margin-left: 5px;
          position: relative;
          top: -1px;
        }
        .points-dropdown :global(> div > button:hover) {
          text-decoration: underline;
        }
        .dropdown-option {
          display: flex;
          justify-content: space-between;
        }
        .dropdown-divider {
          background-color: ${theme.lightGreyDark};
          height: 1px;
          margin: 10px 0;
        }
        .dropdown-info {
          white-space: nowrap;
        }
        .dropdown-info-icon {
          display: inline-block;
          margin-left: 5px;
        }
      `}</style>
    </div>
  );
};

export default TaskPointWeightSelector;
